import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
// ✅ 1. Import the Firebase functions SDK
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { PaperclipIcon, TrashIcon } from '../../components/icons/Icons';

// Helper function to convert a file to a Base64 string
const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
});

// Helper component for form sections
const FormSection = ({ title, children }) => (
    <fieldset className="border-t pt-6 mt-6">
        <legend className="text-lg font-semibold text-[#213368] mb-4">{title}</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {children}
        </div>
    </fieldset>
);

export default function PublicVendorForm() {
    const [isVerified, setIsVerified] = useState(false);
    const [vendorDocId, setVendorDocId] = useState(null);
    const [vendorEmail, setVendorEmail] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        basicInfo: { legalName: '', address: '', contactPerson: '', phone: '', email: '', website: '' },
        corporateInfo: { description: '', entityType: '', registrationNumber: '', pan: '', gst: '', country: '' },
        bankingInfo: { bankName: '', accountNumber: '', beneficiaryName: '', branchAddress: '', branchCode: '', swift: '', specialInstructions: '', paymentEmail: '' }
    });
    const [corporateDocs, setCorporateDocs] = useState([]);
    const [bankProof, setBankProof] = useState(null);
    const [agreedToPolicy, setAgreedToPolicy] = useState(false);
    const appId = process.env.REACT_APP_PROJECT_ID || "admin-c78c6";

    useEffect(() => {
        document.title = "LINC Education Vendor Onboarding Form";
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = '/Brandmark.png';
    }, []);

    const handleVerification = async () => {
        if (!vendorEmail || !inviteCode) {
            setMessage({ type: 'error', text: 'Please enter your email and the access code.' });
            return;
        }
        setIsLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const vendorsRef = collection(db, `/artifacts/${appId}/public/data/vendors`);
            const q = query(vendorsRef,
                where("email", "==", vendorEmail),
                where("inviteCode", "==", inviteCode),
                where("inviteStatus", "==", "Active")
            );
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                throw new Error("Invalid email or access code, or this invitation has already been used. Please contact finance.");
            }

            const vendorDoc = querySnapshot.docs[0];
            setVendorDocId(vendorDoc.id);
            const existingData = vendorDoc.data();
            setFormData(prev => ({
    		...prev,
    		basicInfo: existingData.basicInfo || prev.basicInfo,
    		corporateInfo: existingData.corporateInfo || prev.corporateInfo,
    		bankingInfo: existingData.bankingInfo || prev.bankingInfo,
	     }));
            setIsVerified(true);
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, dataset } = e.target;
        const { section } = dataset;
        setFormData(prev => ({ ...prev, [section]: { ...prev[section], [name]: value } }));
    };

    const handleFileChange = (e, fileType) => {
        const files = Array.from(e.target.files);
        if (fileType === 'corporate') {
            setCorporateDocs(prev => [...prev, ...files]);
        } else if (fileType === 'bank') {
            setBankProof(files[0] || null);
        }
    };

    const removeFile = (index, fileType) => {
        if (fileType === 'corporate') {
            setCorporateDocs(docs => docs.filter((_, i) => i !== index));
        } else if (fileType === 'bank') {
            setBankProof(null);
        }
    };

    const handlePhoneChange = (value) => {
        setFormData(prev => ({
            ...prev,
            basicInfo: { ...prev.basicInfo, phone: value || '' }
        }));
    };

    // ✅ 2. Updated handleSubmit to call the Cloud Function
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreedToPolicy) {
            setMessage({ type: 'error', text: 'You must agree to the Privacy Policy to proceed.' });
            return;
        }
        setIsLoading(true);
        setMessage({ type: '', text: '' });

        try {
            // Convert files to Base64 to send to the function
            const corporateDocsBase64 = await Promise.all(
                corporateDocs.map(async file => ({
                    name: file.name,
                    type: file.type,
                    data: await fileToBase64(file)
                }))
            );

            let bankProofBase64 = null;
            if (bankProof) {
                bankProofBase64 = {
                    name: bankProof.name,
                    type: bankProof.type,
                    data: await fileToBase64(bankProof)
                };
            }
            
            // Call the Cloud Function
            const submitVendorForm = httpsCallable(functions, 'submitPublicVendorForm');
            await submitVendorForm({
                appId,
                vendorDocId,
                inviteCode,
                formData,
                corporateDocs: corporateDocsBase64,
                bankProof: bankProofBase64,
            });

            setIsSubmitted(true);
            setMessage({ type: 'success', text: 'Your information has been successfully submitted for review.' });
        } catch (error) {
            console.error("Public form submission error:", error);
            setMessage({ type: 'error', text: `Submission failed: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    };

    if (isSubmitted) {
        return (
           <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
               <div className="max-w-xl w-full bg-white p-8 rounded-lg shadow-lg text-center">
                   <img src="/Brand_logo.png" alt="LINC Education" className="h-12 mx-auto mb-6" />
                   <h1 className="text-2xl font-bold text-green-600 mb-4">Submission Successful!</h1>
                   <p className="text-gray-700">{message.text}</p>
                   <p className="text-gray-500 mt-4 text-sm">A member of our team will review your details. You can now close this window.</p>
               </div>
           </div>
       );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                   <img src="/Brand_logo.png" alt="LINC Education" className="h-16 mx-auto mb-6" />
                   <h1 className="text-3xl font-bold text-[#213368]">Vendor Onboarding Form</h1>
                   <p className="mt-2 text-lg text-gray-600">Please provide your company details for our records.</p>
                </div>

                <div className="bg-white rounded-lg shadow-xl p-8">
                    {message.text && (
                        <div className={`p-4 mb-4 rounded-md text-center ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                           {message.text}
                        </div>
                    )}

                    {!isVerified ? (
                        <div className="space-y-4">
                            <h2 className="text-xl font-semibold text-[#213368]">Step 1: Verification</h2>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Your Email Address</label>
                                <input
                                   type="email"
                                   value={vendorEmail}
                                   onChange={(e) => setVendorEmail(e.target.value)}
                                   placeholder="Enter the email where you received the invite"
                                   className="mt-1 block w-full md:w-1/2 p-2 border border-gray-300 rounded-md shadow-sm"
                                   required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Access Code</label>
                                <input
                                   value={inviteCode}
                                   onChange={(e) => setInviteCode(e.target.value)}
                                   placeholder="Enter the secret code from your email"
                                   className="mt-1 block w-full md:w-1/2 p-2 border border-gray-300 rounded-md shadow-sm font-mono"
                                   required
                                />
                            </div>
                            <div className="pt-2">
                                <button type="button" onClick={handleVerification} disabled={isLoading} className="px-6 py-2 bg-[#20BEC6] text-white font-bold rounded-lg shadow-md hover:bg-opacity-90 disabled:bg-gray-400">
                                    {isLoading ? 'Verifying...' : 'Verify & Proceed'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <h2 className="text-xl font-semibold text-[#213368] mb-4">Step 2: Enter Your Details</h2>
                            <FormSection title="A. Basic Information">
                                <input data-section="basicInfo" name="legalName" value={formData.basicInfo.legalName} onChange={handleChange} placeholder="Legal Name of Entity" className="md:col-span-2 p-2 border rounded" required />
                                <textarea data-section="basicInfo" name="address" value={formData.basicInfo.address} onChange={handleChange} placeholder="Registered Address with Pin Code" className="md:col-span-2 p-2 border rounded" rows="3" required></textarea>
                                <input data-section="basicInfo" name="contactPerson" value={formData.basicInfo.contactPerson} onChange={handleChange} placeholder="Primary Contact Person" className="p-2 border rounded" required/>
                                {/* --- UPDATED PHONE FIELD --- */}
<div className="md:col-span-1"> {/* This ensures it stays in one column */}
    <PhoneInput
        international
        defaultCountry="IN" // Set your most common country
        placeholder="Phone / Mobile Number"
        value={formData.basicInfo.phone}
        onChange={handlePhoneChange}
        required
    />
</div>
                                <input data-section="basicInfo" name="email" type="email" value={formData.basicInfo.email} onChange={handleChange} placeholder="Official Email Address" className="p-2 border rounded" required/>
                                <input data-section="basicInfo" name="website" value={formData.basicInfo.website} onChange={handleChange} placeholder="Website (optional)" className="p-2 border rounded"/>
                            </FormSection>

                            <FormSection title="B. Corporate Information">
                                <textarea data-section="corporateInfo" name="description" value={formData.corporateInfo.description} onChange={handleChange} placeholder="Brief Company Description" className="md:col-span-2 p-2 border rounded" rows="3"></textarea>
                                <select data-section="corporateInfo" name="entityType" value={formData.corporateInfo.entityType} onChange={handleChange} className="p-2 border rounded" required>
                                    <option value="">Select Entity Type...</option>
                                    <option>Corporation</option><option>Partnership</option><option>Sole Proprietor</option><option>Other</option>
                                </select>
                                <input data-section="corporateInfo" name="registrationNumber" value={formData.corporateInfo.registrationNumber} onChange={handleChange} placeholder="Company Registration Number or ABN" className="p-2 border rounded"/>
                                <input data-section="corporateInfo" name="pan" value={formData.corporateInfo.pan} onChange={handleChange} placeholder="PAN Number (optional)" className="p-2 border rounded"/>
                                <input data-section="corporateInfo" name="gst" value={formData.corporateInfo.gst} onChange={handleChange} placeholder="GST Number (optional)" className="p-2 border rounded"/>
                                <input data-section="corporateInfo" name="country" value={formData.corporateInfo.country} onChange={handleChange} placeholder="Country of Incorporation" className="p-2 border rounded" required />
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Supporting Documents (e.g., PAN, Certificate of Registration or ABN certificate - Mandatory)</label>
                                    <input type="file" multiple onChange={(e) => handleFileChange(e, 'corporate')} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#20BEC6] file:bg-opacity-20 file:text-[#20BEC6] hover:file:bg-opacity-30" />
                                    {corporateDocs.map((file, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm mt-2"><PaperclipIcon /> {file.name} <button type="button" onClick={() => removeFile(i, 'corporate')}><TrashIcon /></button></div>
                                    ))}
                                </div>
                            </FormSection>

                            <FormSection title="C. Banking Information">
                                <input data-section="bankingInfo" name="bankName" value={formData.bankingInfo.bankName} onChange={handleChange} placeholder="Bank Name" className="p-2 border rounded" />
                                <input data-section="bankingInfo" name="accountNumber" value={formData.bankingInfo.accountNumber} onChange={handleChange} placeholder="Account Number" className="p-2 border rounded" />
                                <input data-section="bankingInfo" name="beneficiaryName" value={formData.bankingInfo.beneficiaryName} onChange={handleChange} placeholder="Beneficiary Name" className="p-2 border rounded" />
                                <textarea data-section="bankingInfo" name="branchAddress" value={formData.bankingInfo.branchAddress} onChange={handleChange} placeholder="Bank Branch Address" className="md:col-span-2 p-2 border rounded" rows="2"></textarea>
                                <input data-section="bankingInfo" name="branchCode" value={formData.bankingInfo.branchCode} onChange={handleChange} placeholder="Branch Code (e.g., BSB or IFSC)" className="p-2 border rounded"/>
                                <input data-section="bankingInfo" name="swift" value={formData.bankingInfo.swift} onChange={handleChange} placeholder="SWIFT (optional)" className="p-2 border rounded"/>
                                <input data-section="bankingInfo" name="paymentEmail" type="email" value={formData.bankingInfo.paymentEmail} onChange={handleChange} placeholder="Email for payment matters" className="md:col-span-2 p-2 border rounded" />
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Proof of Bank Account (e.g., Scanned cheque or extract showing name and account number -Mandatory)</label>
                                    <input type="file" onChange={(e) => handleFileChange(e, 'bank')} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#20BEC6] file:bg-opacity-20 file:text-[#20BEC6] hover:file:bg-opacity-30" />
                                    {bankProof && (
                                        <div className="flex items-center justify-between text-sm mt-2"><PaperclipIcon /> {bankProof.name} <button type="button" onClick={() => removeFile(0, 'bank')}><TrashIcon /></button></div>
                                    )}
                                </div>
                            </FormSection>

                            <div className="mt-6 pt-6 border-t">
                                <label className="flex items-center">
                                    <input type="checkbox" checked={agreedToPolicy} onChange={(e) => setAgreedToPolicy(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-[#20BEC6] focus:ring-[#20BEC6]" />
                                    <span className="ml-2 text-sm text-gray-600">
                                        By submitting these documents, I agree to the <a href="https://linceducation.com/privacy_policy.html" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline">Privacy Policy</a>.
                                    </span>
                                </label>
                            </div>

                            <div className="flex justify-end mt-8">
                                <button type="submit" disabled={isLoading} className="px-8 py-3 bg-[#20BEC6] text-white font-bold text-lg rounded-lg shadow-md hover:bg-opacity-90 disabled:bg-gray-400 disabled:cursor-not-allowed">
                                    {isLoading ? 'Submitting...' : 'Submit Information'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
